import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import clsx from 'clsx'
import type { TeamRow } from '@/data/types'
import { Trophy, Medal, Star } from 'lucide-react'

export function TeamLeaderboard({ teams }: { teams: TeamRow[] }) {
  const { t } = useTranslation()
  const sorted = [...teams].sort((a, b) => a.rank - b.rank)

  return (
    <ul className="space-y-2">
      {sorted.map((tm) => (
        <motion.li
          key={tm.id}
          layout
          className={clsx(
            'flex items-center gap-4 rounded-2xl border border-border bg-surface-2/60 px-4 py-3 backdrop-blur',
            tm.rank === 1 && 'ring-1 ring-brand-primary/40',
          )}
        >
          <RankBadge rank={tm.rank} />
          <div className="flex-1">
            <div className="font-display text-base font-semibold text-ink">{tm.name}</div>
            <div className="text-xs text-ink-subtle">
              {t('teams.members', { count: tm.members })}
            </div>
          </div>
          <Metric label={t('teams.incidentsResolved')} value={String(tm.incidentsResolved)} />
          <Metric label="CSAT" value={tm.csat.toFixed(2)} />
          {tm.ordersHandled > 0 && (
            <Metric label={t('teams.orders')} value={String(tm.ordersHandled)} />
          )}
        </motion.li>
      ))}
    </ul>
  )
}

function RankBadge({ rank }: { rank: number }) {
  const Icon = rank === 1 ? Trophy : rank === 2 ? Medal : Star
  const tone =
    rank === 1
      ? 'bg-brand-primary/20 text-brand-primary'
      : rank === 2
        ? 'bg-brand-accent/15 text-brand-accent'
        : 'bg-surface-3/60 text-ink-muted'
  return (
    <span
      className={clsx(
        'grid size-10 place-items-center rounded-xl font-display text-lg font-bold',
        tone,
      )}
    >
      <Icon className="size-4" />
    </span>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-right">
      <div className="font-display text-base font-semibold tabular-nums text-ink">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-ink-subtle">{label}</div>
    </div>
  )
}
